import {Brand} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/brand.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  Button,
  COLORS,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  tabletAndUpLinaria,
  Text,
} from '@aboutyou/ui-components';
import {useBrandToggleFollow} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React from 'react';
import {useAuthenticationPrompt} from '../../../features/Authentication/AuthenticationProvider/useAuthenticationPrompt';
import {useIsMissionsActive} from '../../../features/Coins/utils/useIsMissionsActive';
import {useSubmitMissionProgress} from '../../Coins/utils/useSubmitMissionProgress';

export const BrandPageFollowButton: React.FC<{
  brand: Brand;
  compactMode?: boolean;
  iconColor?: COLORS;
}> = ({brand, compactMode, iconColor = COLORS.Black}) => {
  const {t} = useTranslation();
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const isMissionActive = useIsMissionsActive();
  const submitLikeBrandMission = useSubmitMissionProgress({
    mission: 'likeBrandMission',
  });

  const {isFollowed, toggleFollow} = useBrandToggleFollow({
    brand,
    onFollowSuccess: () => {
      if (isMissionActive) {
        submitLikeBrandMission(brand.id);
      }
    },
  });

  const handleOnFollow = () => {
    showUserAuthenticationPrompt({
      context: 'follow-brand',
      needsCheckoutSession: false,
      onSuccess: () => {
        // Use setTimeout to ensure the authentication state has updated
        setTimeout(() => {
          toggleFollow();
        });
      },
    });
  };

  const label = isFollowed ? t('Following') : t('Follow');

  return (
    <Wrapper
      data-compact-mode={Boolean(compactMode)}
      onClick={handleOnFollow}
      data-testid={isFollowed ? 'LikeRoundButtonFilled' : 'LikeRoundButton'}
      aria-label={undefined}
      variant="floating"
      size="fit-width"
    >
      <Label
        color={compactMode ? 'White' : 'Black'}
        size={compactMode ? 'px12' : 'px10'}
        transform={compactMode ? 'none' : 'uppercase'}
      >
        {label}
      </Label>
      <Icon
        asset={
          isFollowed
            ? GenericIconName.CheckmarkCircle
            : GenericIconName.AddCircleFill
        }
        size={'S'}
        fill={iconColor}
      />
    </Wrapper>
  );
};

const Wrapper = styled(Button)<{$compactMode?: boolean}>`
  &&& {
    --background-color: ${COLORS['Text.White']};
    min-height: 100%;
    padding: 0;
    height: 40px;
    box-shadow: none;

    &[data-compact-mode='true'] {
      --background-color: transparent;
    }

    & > span {
      gap: 5px;
    }

    ${tabletAndUpLinaria(`
      height: 100%;
      
      &[data-compact-mode="false"] {
        padding: 8px 8px 8px 12px;
      }
    `)}
  }
`;

const Label = styled(Text)`
  font-weight: ${FONT_WEIGHTS.mediumHeavy};

  ${tabletAndUpLinaria(`
    font-weight: ${FONT_WEIGHTS.heavy};
  `)}
`;
